import { motion } from 'framer-motion';
import { Button } from '@/components/Button';
import { useRouter } from 'next/router';
import HowItWorksSection from '@/components/HowItWorksSection';
import { SEOMeta } from '@/templates/SEOMeta';
import { useTranslations } from 'next-intl';
import { getI18nProps } from '@/utils/i18n';
import {
    AdjustmentsHorizontalIcon,
    ChartBarIcon,
    MegaphoneIcon,
    PuzzlePieceIcon,
    RocketLaunchIcon,
    UserPlusIcon,
    UsersIcon,
    ArrowRightIcon,
    CheckCircleIcon
} from "@heroicons/react/24/outline";

export default function HowItWorks({ seoMeta }) {
    const router = useRouter();
    const t = useTranslations('howItWorks');

    const steps = [
        {
            number: 1,
            title: t('steps.signUp.title'),
            icon: UserPlusIcon,
            description: t('steps.signUp.description')
        },
        {
            number: 2,
            title: t('steps.launchCampaign.title'),
            icon: RocketLaunchIcon,
            description: t('steps.launchCampaign.description')
        },
        {
            number: 3,
            title: t('steps.integration.title'),
            icon: PuzzlePieceIcon,
            description: t('steps.integration.description')
        },
        {
            number: 4,
            title: t('steps.creatorOnboarding.title'),
            icon: UsersIcon,
            description: t('steps.creatorOnboarding.description')
        },
        {
            number: 5,
            title: t('steps.creatorPromotion.title'),
            icon: MegaphoneIcon,
            description: t('steps.creatorPromotion.description')
        },
        {
            number: 6,
            title: t('steps.conversionTracking.title'),
            icon: ChartBarIcon,
            description: t('steps.conversionTracking.description')
        },
        {
            number: 7,
            title: t('steps.monitorAnalytics.title'),
            icon: AdjustmentsHorizontalIcon,
            description: t('steps.monitorAnalytics.description')
        }
    ];

    return (
        <>
            <SEOMeta
                title={seoMeta?.title || t('seo.title')}
                description={seoMeta?.description || t('seo.description')}
                keywords={seoMeta?.keywords || t('seo.keywords')}
                url={seoMeta?.url || "/how-it-works"}
            />

            <div className="bg-gradient-to-b from-gray-50 to-white">
                <HowItWorksSection steps={steps} />

                {/* Unified CTA Section - Symmetrical */}
                <section className="py-20">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-8">
                            {/* Free Trial CTA */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl p-8 text-white shadow-xl"
                            >
                                <div className="mb-8">
                                    <div className="inline-flex items-center gap-2 bg-blue-500/20 px-4 py-2 rounded-full mb-4">
                                        <CheckCircleIcon className="h-5 w-5" />
                                        <span className="text-sm font-medium">{t('cta.freeTrial.badge')}</span>
                                    </div>
                                    <h2 className="text-2xl font-bold mb-4">{t('cta.freeTrial.title')}</h2>

                                    <div className="space-y-4 mb-8">
                                        <div className="flex items-center">
                                            <ArrowRightIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                                            <span>{t('cta.freeTrial.bullets.trial')}</span>
                                        </div>
                                        <div className="flex items-center">
                                            <ArrowRightIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                                            <span>{t('cta.freeTrial.bullets.features')}</span>
                                        </div>
                                        <div className="flex items-center">
                                            <ArrowRightIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                                            <span>{t('cta.freeTrial.bullets.support')}</span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <Button
                                        gray
                                        onClick={() => router.push('/signup')}
                                        className="w-full px-8 py-4 rounded-xl hover:scale-105"
                                    >
                                        {t('cta.freeTrial.button')}
                                    </Button>
                                    <p className="text-sm text-blue-200 text-center mt-4">
                                        {t('cta.freeTrial.note')}
                                    </p>
                                </div>
                            </motion.div>

                            {/* Detailed Guide CTA */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="bg-gradient-to-br from-green-600 to-green-800 rounded-3xl p-8 text-white shadow-xl border border-gray-700"
                            >
                                <div className="mb-8">
                                    <div
                                        className="inline-flex items-center gap-2 bg-green-700 px-4 py-2 rounded-full mb-4 cursor-pointer hover:bg-gray-600 transition-colors"
                                        onClick={() => router.push('/detailed-overview')}
                                    >
                                        <ArrowRightIcon className="h-5 w-5" />
                                        <span className="text-sm font-medium">{t('cta.overview.badge')}</span>
                                    </div>
                                    <h2 className="text-2xl font-bold mb-4">{t('cta.overview.title')}</h2>

                                    <div className="space-y-4 mb-8">
                                        <div className="flex items-center">
                                            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3 flex-shrink-0" />
                                            <span>{t('cta.overview.bullets.workflow')}</span>
                                        </div>
                                        <div className="flex items-center">
                                            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3 flex-shrink-0" />
                                            <span>{t('cta.overview.bullets.guidance')}</span>
                                        </div>
                                        <div className="flex items-center">
                                            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3 flex-shrink-0" />
                                            <span>{t('cta.overview.bullets.tutorials')}</span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <Button
                                        outline
                                        gray
                                        onClick={() => router.push('/detailed-overview')}
                                        className="w-full px-8 py-4 rounded-xl hover:scale-105"
                                    >
                                        {t('cta.overview.button')}
                                    </Button>
                                    <p className="text-sm text-gray-400 text-center mt-4">
                                        {t('cta.overview.note')}
                                    </p>
                                </div>
                            </motion.div>
                        </div>

                        {/* Simple Trust Indicators */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            className="mt-16 text-center"
                        >
                            {/*<p className="text-gray-600 mb-2">Trusted by 500+ businesses worldwide</p>*/}
                            <div className="flex flex-wrap justify-center items-center gap-8 text-gray-500 text-sm">
                                <span>{t('trust.uptime')}</span>
                                <span>{t('trust.support')}</span>
                                <span>{t('trust.currencies')}</span>
                                <span>{t('trust.noHiddenFees')}</span>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </div>
        </>
    );
}

export async function getStaticProps(ctx) {
  const { getI18nPropsWithSeo } = await import('@/utils/i18n');
  return getI18nPropsWithSeo(ctx, 'howItWorks', '/how-it-works');
}
